import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from './Button';

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;

interface DialogContentProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showClose?: boolean;
  className?: string;
}

const sizeMap = {
  sm: '400px',
  md: '500px',
  lg: '600px',
  xl: '800px',
  full: '95vw',
};

export function DialogContent({
  children,
  title,
  description,
  size = 'md',
  showClose = true,
  className,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 50,
          animation: 'fadeIn var(--motion-duration-fast) var(--motion-ease-emphasis)',
        }}
      />
      <DialogPrimitive.Content
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: sizeMap[size],
          maxHeight: '85vh',
          background: 'var(--bg-primary)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-2xl), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          zIndex: 51,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'scaleInBounce var(--motion-duration-page) var(--motion-ease-emphasis)',
        }}
        className={className}
      >
        {/* Header */}
        {(title || description) && (
          <div
            style={{
              padding: '24px 24px 16px',
              borderBottom: '1px solid var(--border-secondary)',
              background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
            }}
          >
            {title && (
              <DialogPrimitive.Title
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  letterSpacing: '-0.01em',
                  paddingInlineEnd: showClose ? '40px' : '0',
                }}
              >
                {title}
              </DialogPrimitive.Title>
            )}
            {description && (
              <DialogPrimitive.Description
                style={{
                  fontSize: '14px',
                  color: 'var(--text-tertiary)',
                  marginTop: '6px',
                  lineHeight: 1.5,
                }}
              >
                {description}
              </DialogPrimitive.Description>
            )}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
            overscrollBehavior: 'contain',
          }}
        >
          {children}
        </div>

        {/* Close button */}
        {showClose && (
          <DialogPrimitive.Close
            className="ui-icon-btn"
            style={{
              position: 'absolute',
              top: '16px',
              insetInlineEnd: '16px',
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              transition: 'background-color var(--motion-duration-fast) var(--motion-ease-standard), color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = '2px solid var(--border-focus)';
              e.currentTarget.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
            aria-label="Close dialog"
          >
            <X style={{ width: '20px', height: '20px' }} />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export const DialogClose = DialogPrimitive.Close;

// Dialog Footer for action buttons
interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function DialogFooter({ children, style, ...props }: DialogFooterProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '12px',
        padding: '16px 24px',
        borderTop: '1px solid var(--border-secondary)',
        background: 'var(--bg-secondary)',
        marginTop: 'auto',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

// Alert Dialog - for confirmations
interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  variant?: 'default' | 'danger';
  isLoading?: boolean;
}

export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  isLoading = false,
}: AlertDialogProps) {
  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title} description={description} size="sm" showClose={false}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '8px',
          }}
        >
          <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Sheet / Drawer variant
interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  side?: 'left' | 'right' | 'top' | 'bottom';
  title?: string;
  description?: string;
}

export function Sheet({ open, onOpenChange, children, side = 'right', title, description }: SheetProps) {
  const sideStyles: Record<string, React.CSSProperties> = {
    left: {
      left: 0,
      top: 0,
      bottom: 0,
      width: '100%',
      maxWidth: '400px',
      borderRadius: '0 20px 20px 0',
      animation: open ? 'slideInLeft var(--motion-duration-page) var(--motion-ease-emphasis)' : undefined,
    },
    right: {
      right: 0,
      top: 0,
      bottom: 0,
      width: '100%',
      maxWidth: '400px',
      borderRadius: '20px 0 0 20px',
      animation: open ? 'slideInRight var(--motion-duration-page) var(--motion-ease-emphasis)' : undefined,
    },
    top: {
      top: 0,
      left: 0,
      right: 0,
      height: 'auto',
      maxHeight: '80vh',
      borderRadius: '0 0 20px 20px',
      animation: open ? 'fadeInDown var(--motion-duration-page) var(--motion-ease-emphasis)' : undefined,
    },
    bottom: {
      bottom: 0,
      left: 0,
      right: 0,
      height: 'auto',
      maxHeight: '80vh',
      borderRadius: '20px 20px 0 0',
      animation: open ? 'fadeInUp var(--motion-duration-page) var(--motion-ease-emphasis)' : undefined,
    },
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 50,
            animation: 'fadeIn var(--motion-duration-fast) var(--motion-ease-emphasis)',
          }}
        />
        <DialogPrimitive.Content
          style={{
            position: 'fixed',
            background: 'var(--bg-primary)',
            boxShadow: 'var(--shadow-2xl)',
            zIndex: 51,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...sideStyles[side],
          }}
        >
          {/* Handle for bottom sheet */}
          {side === 'bottom' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '12px',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '4px',
                  borderRadius: 'var(--radius-full)',
                  background: '#D1D5DB',
                }}
              />
            </div>
          )}

          {(title || description) && (
            <div
              style={{
                padding: side === 'bottom' ? '8px 24px 16px' : '24px 24px 16px',
                borderBottom: '1px solid var(--border-secondary)',
              }}
            >
              {title && (
                <DialogPrimitive.Title
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                >
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description
                  style={{
                    fontSize: '14px',
                    color: 'var(--text-tertiary)',
                    marginTop: '4px',
                  }}
                >
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          )}

          <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>{children}</div>

          <DialogPrimitive.Close
            className="ui-icon-btn"
            style={{
              position: 'absolute',
              top: side === 'bottom' ? '12px' : '16px',
              insetInlineEnd: '16px',
              width: '32px',
              height: '32px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--bg-tertiary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-tertiary)',
              transition: 'background-color var(--motion-duration-fast) var(--motion-ease-standard), color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = '2px solid var(--border-focus)';
              e.currentTarget.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
            aria-label="Close"
          >
            <X style={{ width: '18px', height: '18px' }} />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
